import type { ItemKeyframes, Keyframe, PropertyKeyframes } from '@/types/keyframe';
import type { TimelineItem } from '@/types/timeline';

type MotionProperty = 'contentX' | 'contentY' | 'scale';
type MotionVariant = 'zoom-in' | 'zoom-out' | 'pan-left' | 'pan-right' | 'pan-up' | 'pan-down';

const MOTION_VARIANTS: MotionVariant[] = [
  'zoom-in',
  'zoom-in',
  'zoom-out',
  'zoom-out',
  'pan-left',
  'pan-right',
  'pan-up',
  'pan-down',
];

const hashString = (value: string): number => {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

const randomFromSeed = (seed: number, offset: number) => {
  const x = Math.sin(seed + offset * 9973) * 10000;
  return x - Math.floor(x);
};

const rangeFromSeed = (seed: number, offset: number, min: number, max: number) =>
  min + randomFromSeed(seed, offset) * (max - min);

const buildKeyframe = (frame: number, value: number, easing: Keyframe['easing']): Keyframe => ({
  id: crypto.randomUUID(),
  frame,
  value,
  easing,
});

const buildProgressFrames = (endFrame: number) => {
  const progressPoints = [0, 0.25, 0.5, 0.75, 1];
  return progressPoints.map((progress, index) => {
    if (index === progressPoints.length - 1) return endFrame;
    return clampFrame(Math.round(endFrame * progress), endFrame);
  });
};

const getMotionSeed = (item: TimelineItem) => {
  const sourceKey = 'src' in item && typeof item.src === 'string' ? item.src : '';
  return hashString([
    item.originId || '',
    item.mediaId || '',
    sourceKey,
    item.label || '',
    item.id,
  ].join('|'));
};

const clampFrame = (frame: number, endFrame: number) => Math.max(0, Math.min(endFrame, frame));

export const buildClipMotionProperties = (
  item: TimelineItem,
  fps: number = 30
): PropertyKeyframes[] => {
  if (item.type !== 'image') return [];
  if (!item.transform) return [];

  const endFrame = Math.max(0, item.durationInFrames - 1);
  if (endFrame <= 0) return [];

  const seed = getMotionSeed(item);
  const [startFrame, earlyFrame, midFrame, lateFrame, finalFrame] = buildProgressFrames(endFrame);
  const variant = MOTION_VARIANTS[Math.floor(randomFromSeed(seed, 2) * MOTION_VARIANTS.length) % MOTION_VARIANTS.length];
  const zoomBase = rangeFromSeed(seed, 3, 1.28, 1.42);
  const zoomTravel = rangeFromSeed(seed, 4, 0.3, 0.48);
  const panScaleStart = rangeFromSeed(seed, 5, 1.32, 1.48);
  const panScaleEnd = panScaleStart + rangeFromSeed(seed, 6, 0.18, 0.28);
  const width = Math.max(1, item.transform.width ?? 0);
  const height = Math.max(1, item.transform.height ?? 0);
  const panDistanceX = width * rangeFromSeed(seed, 7, 0.03, 0.065);
  const panDistanceY = height * rangeFromSeed(seed, 8, 0.03, 0.065);
  const midBias = rangeFromSeed(seed, 10, 0.22, 0.34);
  const lateBias = rangeFromSeed(seed, 11, 0.62, 0.78);
  const bounceBias = rangeFromSeed(seed, 12, 0.92, 1.08);
  const scaleEarly = panScaleStart + (panScaleEnd - panScaleStart) * midBias;
  const scaleMid = panScaleStart + (panScaleEnd - panScaleStart) * lateBias;
  const scaleLate = panScaleStart + (panScaleEnd - panScaleStart) * bounceBias;
  const driftX = panDistanceX * rangeFromSeed(seed, 13, 0.22, 0.44);
  const driftY = panDistanceY * rangeFromSeed(seed, 14, 0.22, 0.44);
  void fps;

  const properties: Array<{ property: MotionProperty; keyframes: Keyframe[] }> = [];

  if (variant === 'zoom-in') {
    const zoomTarget = zoomBase + zoomTravel;
    properties.push({
      property: 'scale',
      keyframes: [
        buildKeyframe(startFrame, 1.0, 'ease-in-out'),
        buildKeyframe(finalFrame, zoomTarget, 'ease-in-out'),
      ],
    });
    properties.push({
      property: 'contentX',
      keyframes: [
        buildKeyframe(startFrame, -driftX * 0.3, 'ease-in-out'),
        buildKeyframe(finalFrame, driftX * 0.3, 'ease-in-out'),
      ],
    });
    properties.push({
      property: 'contentY',
      keyframes: [
        buildKeyframe(startFrame, driftY * 0.3, 'ease-in-out'),
        buildKeyframe(finalFrame, -driftY * 0.3, 'ease-in-out'),
      ],
    });
    return properties;
  }

  if (variant === 'zoom-out') {
    const startScale = zoomBase + zoomTravel;
    properties.push({
      property: 'scale',
      keyframes: [
        buildKeyframe(startFrame, startScale, 'ease-in-out'),
        buildKeyframe(finalFrame, 1.0, 'ease-in-out'),
      ],
    });
    properties.push({
      property: 'contentX',
      keyframes: [
        buildKeyframe(startFrame, driftX * 0.3, 'ease-in-out'),
        buildKeyframe(finalFrame, -driftX * 0.3, 'ease-in-out'),
      ],
    });
    properties.push({
      property: 'contentY',
      keyframes: [
        buildKeyframe(startFrame, -driftY * 0.3, 'ease-in-out'),
        buildKeyframe(finalFrame, driftY * 0.3, 'ease-in-out'),
      ],
    });
    return properties;
  }

  if (variant === 'pan-left' || variant === 'pan-right') {
    const startX = variant === 'pan-left' ? panDistanceX : -panDistanceX;
    const endX = variant === 'pan-left' ? -panDistanceX : panDistanceX;
    properties.push({
      property: 'scale',
      keyframes: [
        buildKeyframe(startFrame, panScaleStart, 'ease-in-out'),
        buildKeyframe(finalFrame, panScaleEnd, 'ease-in-out'),
      ],
    });
    properties.push({
      property: 'contentX',
      keyframes: [
        buildKeyframe(startFrame, startX, 'ease-in-out'),
        buildKeyframe(finalFrame, endX, 'ease-in-out'),
      ],
    });
    properties.push({
      property: 'contentY',
      keyframes: [
        buildKeyframe(startFrame, 0, 'ease-in-out'),
        buildKeyframe(finalFrame, 0, 'ease-in-out'),
      ],
    });
    return properties;
  }

  const startY = variant === 'pan-up' ? panDistanceY : -panDistanceY;
  const endY = variant === 'pan-up' ? -panDistanceY : panDistanceY;
  properties.push({
    property: 'scale',
    keyframes: [
      buildKeyframe(startFrame, panScaleStart, 'ease-in-out'),
      buildKeyframe(finalFrame, panScaleEnd, 'ease-in-out'),
    ],
  });
  properties.push({
    property: 'contentY',
    keyframes: [
      buildKeyframe(startFrame, startY, 'ease-in-out'),
      buildKeyframe(finalFrame, endY, 'ease-in-out'),
    ],
  });
  properties.push({
    property: 'contentX',
    keyframes: [
      buildKeyframe(startFrame, 0, 'ease-in-out'),
      buildKeyframe(finalFrame, 0, 'ease-in-out'),
    ],
  });
  return properties;
};

export const buildClipMotionEntry = (item: TimelineItem, fps: number = 30): ItemKeyframes | null => {
  const properties = buildClipMotionProperties(item, fps);
  if (properties.length === 0) return null;

  return {
    itemId: item.id,
    properties,
  };
};
